import { showAddOrEditApprovalStructureState, systemRoles } from "@/atoms";
import CustomInput from "@/components/common/inputs/CustomInput";
import StyledSelect from "@/components/common/Select.styled";
import MainModal from "@/components/modals/MainModal";
import { useCreateApprovalStructure, useEditApprovalStructure } from "@/services/approval-structures";
import { useGetAllCouncils } from "@/services/councils";
import { formatUnderScoreText } from "@/utils";
import { Button, Select } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useRecoilState } from "recoil";

const AddUpdateApprovalStructure = () => {
  const [show, setShow] = useRecoilState(showAddOrEditApprovalStructureState);
  const [roles] = useRecoilState(systemRoles);
  const queryClient = useQueryClient();

  const { data: councils } = useGetAllCouncils();

  const {
    mutate: createApprovalStructure,
    isPending: isCreating
  } = useCreateApprovalStructure();

  const {
    mutate: editApprovalStructure,
    isPending: isEditing
  } = useEditApprovalStructure();

  const [formData, setFormData] = useState({
    council_id: show?.approvalStructure?.council_id ?? "",
    role_id: show?.approvalStructure?.role_id ?? "",
    approvalLevel: show?.approvalStructure?.approvalLevel ?? 1,
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (show?.action === 'add') {
      createApprovalStructure(formData, {
        onSuccess: (data) => {
          if (data.success) {
            notifications.show({
              title: "Success",
              message: "Approval Structure created successfully",
              color: "green",
            });
            queryClient.invalidateQueries({
              queryKey: ['approval-structures']
            });
            setShow(null);
          } else {
            notifications.show({
              title: "Error",
              message: data.message || "Something went wrong",
              color: "red",
            });
          }
        },
        onError: (error) => {
          notifications.show({
            title: "Error",
            message: error.message || "An error occurred",
            color: "red",
          });
        },
      });
    } else if (show?.action === 'edit' && show.approvalStructure?.id) {
      editApprovalStructure({
        ...formData,
        id: show.approvalStructure.id
      }, {
        onSuccess: (data) => {
          if (data.success) {
            notifications.show({
              title: "Success",
              message: "Approval Structure updated successfully",
              color: "green",
            });
            queryClient.invalidateQueries({
              queryKey: ['approval-structures']
            });
            setShow(null);
          } else {
            notifications.show({
              title: "Error",
              message: data.message || "Something went wrong",
              color: "red",
            });
          }
        },
        onError: (error) => {
          notifications.show({
            title: "Error",
            message: error.message || "An error occurred",
            color: "red",
          });
        },
      });
    }
  };

  const isFormValid = formData.council_id && formData.role_id && formData.approvalLevel > 0;

  return (
    <MainModal
      open={Boolean(show?.show)}
      onClose={() => setShow(null)}
      title={show?.action === 'add' ? 'Add Approval Structure' : 'Edit Approval Structure'}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-4">
        <CustomInput label="Select Council" alwaysFocused>
          <Select
            value={formData.council_id ?? ""}
            onChange={(val) => setFormData({ ...formData, council_id: val ?? "" })}
            data={councils?.data.map((item) => ({
              value: item.id,
              label: item.councilName,
            }))}
            placeholder="Select Your Council"
            variant="unstyled"
            width="100%"
            className="w-full"
          />
        </CustomInput>

        <CustomInput label="Role" className="w-full" alwaysFocused>
          <StyledSelect
            checkIconPosition="right"
            value={formData.role_id}
            data={roles.map((el) => ({
              value: el.roleId,
              label: formatUnderScoreText(el.roleName),
            }))}
            placeholder={`Select Role`}
            className="w-full"
            onChange={(value: string | null) => {
              setFormData({ ...formData, role_id: value! });
            }}
          />
        </CustomInput>

        <CustomInput
          type="number"
          label="Approval Level"
          value={formData.approvalLevel}
          onChange={(e) => setFormData({
            ...formData,
            approvalLevel: Math.max(1, parseInt(e.target.value) || 1)
          })}
          min={1}
          required
          placeholder="Enter approval level"
        />

        <div className="flex justify-end gap-4 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => setShow(null)}
            disabled={isCreating || isEditing}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={isCreating || isEditing}
            disabled={!isFormValid}
          >
            {show?.action === 'add' ? 'Create' : 'Update'}
          </Button>
        </div>
      </form>
    </MainModal>
  );
};

export default AddUpdateApprovalStructure;
